import {
  BookOpenTextIcon,
  CameraIcon,
  ChatCircleDotsIcon,
  ChatCircleIcon,
  CompassIcon,
  CursorClickIcon,
  FileMagnifyingGlassIcon,
  FilePlusIcon,
  FolderOpenIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  GlobeIcon,
  GraduationCapIcon,
  type Icon,
  LinkSimpleIcon,
  ListBulletsIcon,
  ListChecksIcon,
  MagnifyingGlassIcon,
  NotePencilIcon,
  PlugsIcon,
  TerminalWindowIcon,
  TreeStructureIcon,
  WrenchIcon,
} from '@phosphor-icons/react';

export interface ToolMeta {
  icon: Icon;
  /** Sentence-case, human-readable action title (spec §11: name things by what happened). */
  humanTitle: string;
}

/**
 * Friendly presentation for the coding agent's built-in tools (see
 * `backend/crates/coding-agent/src/tools`). Aliases (`read_file`) map to the same entry as their
 * canonical name so a rename on either side degrades gracefully. Unknown tools fall back to
 * {@link toolMeta}'s generic wrench + the raw tool name.
 */
const TOOL_META: Record<string, ToolMeta> = {
  bash: { icon: TerminalWindowIcon, humanTitle: 'Ran' },
  read: { icon: FileMagnifyingGlassIcon, humanTitle: 'Read file' },
  read_file: { icon: FileMagnifyingGlassIcon, humanTitle: 'Read file' },
  write: { icon: FilePlusIcon, humanTitle: 'Wrote file' },
  edit: { icon: NotePencilIcon, humanTitle: 'Edited file' },
  grep: { icon: MagnifyingGlassIcon, humanTitle: 'Searched code' },
  find: { icon: FolderOpenIcon, humanTitle: 'Found files' },
  ls: { icon: ListBulletsIcon, humanTitle: 'Listed directory' },
  web_search: { icon: GlobeIcon, humanTitle: 'Searched the web' },
  code_context: { icon: BookOpenTextIcon, humanTitle: 'Looked up library docs' },
  web_fetch: { icon: LinkSimpleIcon, humanTitle: 'Fetched page' },
  skill: { icon: GraduationCapIcon, humanTitle: 'Used skill' },
  // Normally rendered by `TodoToolCard` instead; this is the fallback for a payload that
  // can't be parsed into a checklist.
  set_todos: { icon: ListChecksIcon, humanTitle: 'Updated todos' },
  // Normally rendered by `AskUserToolCard` instead; this is the fallback for a payload that
  // can't be parsed into questions.
  ask_user: { icon: ChatCircleDotsIcon, humanTitle: 'Asked you' },
  browser_open: { icon: CompassIcon, humanTitle: 'Opened page' },
  browser_snapshot: { icon: TreeStructureIcon, humanTitle: 'Read the page' },
  // Same icon as the preview element picker: both mean "a thing on the page was pointed at".
  browser_act: { icon: CursorClickIcon, humanTitle: 'Interacted' },
  browser_screenshot: { icon: CameraIcon, humanTitle: 'Took a screenshot' },

  // The built-in repository connector (`services/mcp/repo.rs`). Keyed by the full namespaced
  // name, which is what the agent emits, so these win over the generic "Repo · <tool>" the
  // fallback would produce. Opening a pull request is the most visible thing an agent does
  // here — it should not read like a raw MCP call.
  mcp__repo__create_pull_request: { icon: GitPullRequestIcon, humanTitle: 'Opened pull request' },
  mcp__repo__update_pull_request: { icon: GitPullRequestIcon, humanTitle: 'Updated pull request' },
  mcp__repo__merge_pull_request: { icon: GitMergeIcon, humanTitle: 'Merged pull request' },
  mcp__repo__close_pull_request: { icon: GitPullRequestIcon, humanTitle: 'Closed pull request' },
  mcp__repo__get_pull_request: { icon: GitPullRequestIcon, humanTitle: 'Read pull request' },
  mcp__repo__list_pull_requests: { icon: GitPullRequestIcon, humanTitle: 'Listed pull requests' },
  mcp__repo__list_pull_checks: { icon: ListChecksIcon, humanTitle: 'Checked CI' },
  mcp__repo__list_pull_reviews: { icon: ChatCircleIcon, humanTitle: 'Read reviews' },
  mcp__repo__list_pull_comments: { icon: ChatCircleIcon, humanTitle: 'Read PR comments' },
};

/**
 * Split a connector tool name into its parts.
 *
 * Connector tools reach us namespaced as `mcp__<connector>__<tool>`. Anything else is a
 * built-in, and comes back with no connector.
 */
export function describeToolName(name: string): { connector: string | null; tool: string } {
  const rest = name.startsWith('mcp__') ? name.slice('mcp__'.length) : null;
  if (!rest) return { connector: null, tool: name };

  const sep = rest.indexOf('__');
  if (sep <= 0 || sep + 2 >= rest.length) return { connector: null, tool: name };

  return { connector: titleCase(rest.slice(0, sep)), tool: rest.slice(sep + 2) };
}

/** `acme_corp` → `Acme corp`. Good enough for a label; `github` reads as `Github`. */
function titleCase(slug: string): string {
  const words = slug.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function toolMeta(name: string): ToolMeta {
  const known = TOOL_META[name];
  if (known) return known;

  // Connector tools render as "Acme · list_things" rather than the raw mangled name. Without
  // this they fall through to the generic wrench plus `mcp__acme__list_things`, which is
  // readable but noisy.
  const { connector, tool } = describeToolName(name);
  if (connector) return { icon: PlugsIcon, humanTitle: `${connector} · ${tool}` };

  return { icon: WrenchIcon, humanTitle: name };
}

/** The argument key that best identifies what a tool call actually did, mirroring the one-line
 * summary the TUI shows (`tool_line::summary` in `backend/crates/coding-agent`). */
const SUMMARY_KEY: Record<string, string> = {
  bash: 'command',
  read: 'path',
  read_file: 'path',
  write: 'path',
  edit: 'path',
  ls: 'path',
  grep: 'pattern',
  find: 'pattern',
  web_search: 'query',
  code_context: 'query',
  web_fetch: 'url',
  skill: 'name',
  browser_open: 'url',
  browser_act: 'element',
  mcp__repo__create_pull_request: 'title',
  mcp__repo__update_pull_request: 'title',
};

/** Fallback argument per tool, for when the preferred one is absent. */
const SUMMARY_FALLBACK_KEY: Record<string, string> = {
  // `element` is a courtesy the model supplies for display; a bare ref still beats a blank row.
  browser_act: 'ref',
};

/** A one-line summary of what a tool call ran/targeted (e.g. the shell command or file path), for
 * display next to the generic `humanTitle`. `null` when the tool has no such argument. */
export function summarizeToolInput(name: string, input: unknown): string | null {
  const key = SUMMARY_KEY[name];
  if (!key || typeof input !== 'object' || input === null) return null;
  const record = input as Record<string, unknown>;
  const value = record[key] ?? record[SUMMARY_FALLBACK_KEY[name] ?? ''];
  if (typeof value !== 'string' || !value) return null;
  return value.replace(/\n/g, ' ⏎ ');
}
